import {
  DietaryPattern,
  FoodRestriction,
  IngredientIDObject,
  Recipe,
  RecipeTag,
  UserIngredientFeedback,
  UserRecipeFeedback,
} from '@/api/types'
import { Label } from '@/components/Label'
import AllergensEditor from '@/features/recipes/AllergensEditor'
import { DietType, groupTags } from '@/features/recipes/helpers'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IngredientsSearch, { Ing } from './IngredientsSearch'
import RecipesSearch from './RecipesSearch'
import { Radio, RadioGroup } from '@/components/RadioGroup'
import { R } from '@tanstack/react-query-devtools/build/legacy/ReactQueryDevtools-Cn7cKi7o'

type Props = {
  tags: RecipeTag[]
  allergens: FoodRestriction[]
  selectedAllergens: FoodRestriction[]
  selectedDiets: DietaryPattern[]
  onChangeAllergens: (allergens: FoodRestriction[]) => void
  onChangeDiets: (diets: DietaryPattern[]) => void
  onChangeFavoriteIngredients: (ingredients: UserIngredientFeedback[]) => void
  onChangeFavoriteRecipes: (recipes: UserRecipeFeedback[]) => void
  onChangeRestrictIngredients: (recipes: IngredientIDObject[]) => void
}

type SelectedDiet = DietaryPattern | 'NONE'

export default function FoodPreferences({
  tags,
  allergens,
  selectedAllergens,
  selectedDiets,
  onChangeAllergens,
  onChangeDiets,
  onChangeFavoriteIngredients,
  onChangeRestrictIngredients,
  onChangeFavoriteRecipes,
}: Props) {
  const { t } = useTranslation()
  const groupedTags = useMemo(() => groupTags(tags), [tags])

  function handleChangeAllergens(selectedAllergens: string[]) {
    const foodRestrictions = allergens.filter((allergen) =>
      selectedAllergens.includes(allergen.name),
    )

    onChangeAllergens(foodRestrictions)
  }

  const handleFavIngredientsChange = useCallback(
    (ingredients: Ing[]) => {
      onChangeFavoriteIngredients(
        ingredients.map((ing) => ({
          value: 1,
          ingredient: { idIngredient: ing.idIngredient, name: ing.name },
        })),
      )
    },
    [onChangeFavoriteIngredients],
  )

  const handleFavRecipesChange = useCallback(
    (recipes: Recipe[]) => {
      onChangeFavoriteRecipes(
        recipes.map((recipe) => ({
          value: 1,
          recipe: { idRecipe: recipe.idRecipe, name: recipe.name },
        })),
      )
    },
    [onChangeFavoriteRecipes],
  )

  const handleChangeDiets = useCallback(
    (value: SelectedDiet) => {
      const selectedDiet = value === 'NONE' ? [] : [value]
      onChangeDiets(selectedDiet)
    },
    [onChangeDiets],
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <AllergensEditor
        allergens={allergens}
        selectedAllergens={selectedAllergens.map((allergen) => allergen.name)}
        onChange={handleChangeAllergens}
      />
      <Label>{t('recipe.dietRestrictions')}</Label>
      <RadioGroup
        //isDisabled={isDisabled}
        variation="segmented"
        value={selectedDiets.length > 0 ? selectedDiets[0] : 'NONE'}
        onChange={(value: SelectedDiet) => handleChangeDiets(value)}>
        <Radio value="NONE">{t('recipe.dietOptions.NONE')}</Radio>
        {groupedTags.DIET.map((diet: RecipeTag) => (
          <Radio key={diet.name} value={diet.name}>
            {t(`recipe.dietOptions.${diet.name as DietType}`)}
          </Radio>
        ))}
      </RadioGroup>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <Label>{t('foodPreferences.favoriteIngredients')}</Label>
          <IngredientsSearch onChange={handleFavIngredientsChange} />
        </div>
        <div>
          <Label>{t('foodPreferences.restrictIngredients')}</Label>
          <IngredientsSearch onChange={onChangeRestrictIngredients} />
        </div>
        <div>
          <Label>{t('foodPreferences.favoriteRecipes')}</Label>
          <RecipesSearch onChange={handleFavRecipesChange} />
        </div>
      </div>
    </div>
  )
}
